import React, { useState } from "react";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

/**
 * BookFlipbook — a hardcover-style book with a real page-turn animation.
 *
 * Layout:
 *   - Left half = the "spine side" (always shows the previous face)
 *   - Right half = the flippable page (rotates -180° around its left edge)
 * pages: array of React nodes rendered as full-page faces (index 0 = cover).
 *
 * Adjacent-page rendering:
 *   Behind the flipping page we render the NEXT page so when the flip
 *   completes, the reader sees the next face already in position.
 */
export const BookFlipbook = ({ pages, testId = "book-flip", ariaLabel = "The Love Genome book" }) => {
  const [i, setI] = useState(0);          // current top-page index
  const [flipping, setFlipping] = useState(null); // "next" | "prev" | null

  const total = pages?.length || 0;
  if (!total) return null;

  const go = (dir) => {
    if (flipping) return;
    if (dir === "next" && i >= total - 1) return;
    if (dir === "prev" && i <= 0) return;
    setFlipping(dir);
    setTimeout(() => {
      setI((n) => (dir === "next" ? n + 1 : n - 1));
      setFlipping(null);
    }, 900); // matches CSS duration below
  };

  // Faces around the flipping page
  const behind = flipping === "next" ? pages[i + 1] : flipping === "prev" ? pages[i - 1] : pages[i];
  const top = pages[i];
  const topRotation =
    flipping === "next" ? -180 : flipping === "prev" ? 0 : 0;
  const behindZ = 1;
  const topZ = 3;

  return (
    <div
      className={`bookflip relative ${flipping ? "flipping" : ""}`}
      aria-label={ariaLabel}
      data-testid={testId}
      style={{ perspective: "2400px", touchAction: "manipulation" }}
    >
      <div
        className="bookflip__stage relative"
        style={{
          width: "100%",
          aspectRatio: "3 / 4",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Deeper page (the one that becomes visible after flip) */}
        <div
          className="absolute inset-0 border border-gold/40"
          style={{
            background: "var(--cream, #FAFAFA)",
            boxShadow: "inset 6px 0 22px rgba(0,0,0,0.18)",
            zIndex: behindZ,
          }}
        >
          {behind}
        </div>

        {/* Top page — flips */}
        <div
          className="absolute inset-0 border border-gold/40"
          style={{
            transformOrigin: "left center",
            transformStyle: "preserve-3d",
            transform: `rotateY(${topRotation}deg)`,
            transition: "transform 0.9s cubic-bezier(0.76, 0, 0.24, 1)",
            zIndex: topZ,
            boxShadow:
              "0 30px 60px -20px rgba(0,0,0,0.5), inset -8px 0 24px rgba(0,0,0,0.12)",
          }}
        >
          {/* Front of the top page */}
          <div
            className="absolute inset-0"
            style={{ backfaceVisibility: "hidden", background: "var(--cream, #FAFAFA)" }}
          >
            {top}
          </div>
          {/* Back of the top page (visible mid-flip, holds the next page mirrored) */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              background: "var(--cream, #FAFAFA)",
            }}
          >
            {flipping === "next" ? pages[i + 1] : flipping === "prev" ? pages[i] : null}
          </div>
        </div>

        {/* Spine gutter shadow */}
        <div
          aria-hidden
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{
            left: 0,
            width: 14,
            background: "linear-gradient(to right, rgba(0,0,0,0.18), transparent)",
            zIndex: 4,
          }}
        />
      </div>

      {/* Controls */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={() => go("prev")}
          disabled={!!flipping || i === 0}
          className="flex items-center gap-1.5 font-body text-xs uppercase tracking-widest text-ink/60 hover:text-emerald disabled:opacity-30"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>

        <div className="flex items-center gap-1.5" data-testid={`${testId}-progress`}>
          {pages.map((_, idx) => (
            <span
              key={idx}
              className={`h-1.5 rounded-full transition-all ${
                idx === i ? "w-6 bg-gold" : "w-1.5 bg-ink/20"
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => go("next")}
          disabled={!!flipping || i >= total - 1}
          className="flex items-center gap-1.5 font-body text-xs uppercase tracking-widest text-ink/60 hover:text-emerald disabled:opacity-30"
          data-testid={`${testId}-next`}
        >
          {i === 0 ? "Open the book" : "Next page"} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Prebuilt "page" renderers for the home hero
// ---------------------------------------------------------------------------

export const BookCoverFace = ({ src, alt = "The Love Genome — Volume I" }) => (
  <img
    src={src}
    alt={alt}
    draggable={false}
    className="w-full h-full object-cover object-center block"
    style={{ pointerEvents: "none" }}
  />
);

export const BookPageFace = ({
  eyebrow,
  title,
  paragraphs = [],
  pageNumber,
  align = "left",
}) => (
  <div
    className="w-full h-full flex flex-col justify-between p-6 md:p-10"
    style={{
      background:
        "radial-gradient(120% 90% at 10% 0%, #FFFDF6 0%, #F5F1E5 55%, #EDE7D5 100%)",
      color: "#1F2A22",
    }}
  >
    {/* Top ornament */}
    <div className="flex items-center justify-center gap-3 pb-4 border-b border-gold/50">
      <span className="w-8 h-px bg-gold/70" />
      <BookOpen className="w-4 h-4 text-gold" />
      <span className="w-8 h-px bg-gold/70" />
    </div>

    <div className={`flex-1 flex flex-col justify-center ${align === "center" ? "items-center text-center" : ""}`}>
      {eyebrow && (
        <p className="uppercase tracking-[0.32em] text-[10px] text-emerald mb-3 font-body">
          {eyebrow}
        </p>
      )}
      {title && (
        <h3
          className="font-display text-emerald leading-tight mb-4"
          style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)" }}
        >
          {title}
        </h3>
      )}
      <div
        className="space-y-3 font-editorial text-ink"
        style={{
          fontSize: "clamp(0.75rem, 0.95vw, 0.95rem)",
          lineHeight: 1.7,
          maxWidth: "38ch",
        }}
      >
        {paragraphs.map((p, k) => (
          <p key={k}>{p}</p>
        ))}
      </div>
    </div>

    {/* Page footer */}
    <div className="flex items-center justify-between pt-4 border-t border-gold/50 font-body text-[10px] uppercase tracking-widest text-ink/50">
      <span>The Love Genome™</span>
      {pageNumber && <span>· {pageNumber} ·</span>}
      <span>Felicia T. Johnson</span>
    </div>
  </div>
);

export default BookFlipbook;
